import { useCallback } from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Menu,
    MenuItem,
    MenuRoot,
    MenuTrigger,
    Popover,
    Separator,
} from '@gitbook/spine-react';

import type { LibraryItem } from './LibraryItems';
import { useLibraryNavigationState } from './ViewNavigation';

export type SortBy = 'name' | 'size' | 'updated';
export type SortDirection = 'asc' | 'desc';

export function useSortState(props: {
    spaceId: string;
    scope: 'files' | 'images' | 'reusableContents' | 'variables' | 'tags';
    defaultSortBy: SortBy;
    defaultSortDirection: SortDirection;
}) {
    const { spaceId, scope, defaultSortBy, defaultSortDirection } = props;
    const [state, setState] = useLibraryNavigationState({ spaceId });
    const sortBy = state[scope]?.sortBy ?? defaultSortBy;
    const sortDirection = state[scope]?.sortDirection ?? defaultSortDirection;
    const setSort = useCallback(
        (sortBy: SortBy, sortDirection: SortDirection) => {
            setState((prev) => ({ ...prev, [scope]: { ...prev[scope], sortBy, sortDirection } }));
        },
        [setState, scope]
    );
    return { sortBy, sortDirection, setSort };
}

export function SortButton(props: {
    sortBy: SortBy;
    sortDirection: SortDirection;
    options: SortBy[];
    onChange: (sortBy: SortBy, sortDirection: SortDirection) => void;
}) {
    const { sortBy, sortDirection, onChange, options } = props;
    return (
        <MenuRoot>
            <MenuTrigger>
                <Button
                    kind="blank"
                    label="Sort"
                    leadingIcon={sortDirection === 'desc' ? Icon.SortUp : Icon.SortDown}
                    tooltip={{
                        label: getSortLabel({ sortBy, sortDirection }),
                        description: 'Click to change sorting',
                    }}
                    iconOnly
                />
            </MenuTrigger>
            <Popover>
                <Menu>
                    {options.flatMap((option, index) => {
                        return [
                            ...(['asc', 'desc'] as const).map((direction) => {
                                return (
                                    <MenuItem
                                        key={`${option}-${direction}`}
                                        icon={direction === 'asc' ? Icon.SortDown : Icon.SortUp}
                                        checked={sortBy === option && sortDirection === direction}
                                        onAction={() => onChange(option, direction)}
                                    >
                                        {getSortLabel({ sortBy: option, sortDirection: direction })}
                                    </MenuItem>
                                );
                            }),
                            index < options.length - 1 && <Separator key={option} />,
                        ];
                    })}
                </Menu>
            </Popover>
        </MenuRoot>
    );
}

function getSortLabel(args: { sortBy: SortBy; sortDirection: SortDirection }): string {
    const { sortBy, sortDirection } = args;
    switch (sortBy) {
        case 'updated':
            return sortDirection === 'desc' ? 'Updated newest first' : 'Updated oldest first';
        case 'name':
            return sortDirection === 'asc' ? 'Name A to Z' : 'Name Z to A';
        case 'size':
            return sortDirection === 'desc' ? 'Size largest first' : 'Size smallest first';
    }
}

export function sortLibraryItems<T extends LibraryItem>(
    items: T[],
    args: { sortBy: SortBy; sortDirection: SortDirection }
): T[] {
    const { sortBy, sortDirection } = args;
    const sortedItems = [...items];
    sortedItems.sort((left, right) => {
        let result = 0;
        switch (sortBy) {
            case 'name':
                result = left.title.localeCompare(right.title, undefined, { sensitivity: 'base' });
                break;
            case 'size': {
                if (
                    (left.type === 'file' || left.type === 'image') &&
                    (right.type === 'file' || right.type === 'image')
                ) {
                    result = left.file.size - right.file.size;
                }
                break;
            }
            case 'updated':
                result = left.lastUpdatedAt - right.lastUpdatedAt;
                break;
        }
        return sortDirection === 'asc' ? result : -result;
    });
    return sortedItems;
}
